import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Pencil } from 'lucide-react';
import { SmsConsentCheckbox } from '../SmsConsent';

/**
 * CheckoutContactFields - Contact form fields for checkout
 *
 * Provides two modes:
 * 1. READ-ONLY MODE: When all fields (name, email, phone) are pre-filled,
 *    shows a summary view with an Edit button. User must click Edit to modify.
 * 2. EDITABLE MODE: Shows input fields for name, email, phone when:
 *    - Any field is empty/missing
 *    - User clicked the Edit button
 *
 * WHY: Pre-filled contact info shouldn't require re-confirmation. Users coming
 * from login already have this data - showing it as read-only reduces friction.
 */
export default function CheckoutContactFields({
  value,
  onChange,
  disabled = false,
  autoFocus = false,
  className = '',
  smsConsentAccepted = false,
  onSmsConsentChange,
  smsConsentError = '',
  smsConsentId = 'checkout-contact-sms-consent',
  showSmsConsent = true,
  collapseWhenComplete = true,
  editBehavior = 'draft',
  invalidFields = {},
}) {
  /**
   * Determines if all fields have meaningful values (non-empty, non-whitespace)
   */
  const allFieldsFilled =
    value?.name?.trim() &&
    value?.email?.trim() &&
    value?.phone?.trim();

  /**
   * Track whether user wants to edit (clicked Edit button)
   * Only applicable when allFieldsFilled is true
   */
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState({
    name: value?.name ?? '',
    email: value?.email ?? '',
    phone: value?.phone ?? '',
  });

  /**
   * Track if this is the first render with all fields filled
   * Used to prevent resetting isEditing when value changes externally
   */
  const wasEditingRef = useRef(false);
  const editSnapshotRef = useRef(null);
  const firstInputRef = useRef(null);
  const usesDraftEditing = editBehavior !== 'live';

  /**
   * When value changes externally and user was already editing,
   * maintain editing state. This handles cases like:
   * - Parent component updates value while user is typing
   */
  useEffect(() => {
    if (wasEditingRef.current) {
      // User was already editing, maintain that state
      return;
    }
    // Otherwise, reset to collapsed state if all fields are filled
    if (collapseWhenComplete && allFieldsFilled) {
      setIsEditing(false);
    }
  }, [allFieldsFilled, collapseWhenComplete]);

  /**
   * Track editing state for the effect above
   */
  useEffect(() => {
    wasEditingRef.current = isEditing;
  }, [isEditing]);

  useEffect(() => {
    if (!usesDraftEditing && isEditing) {
      firstInputRef.current?.focus();
    }
  }, [isEditing, usesDraftEditing]);

  const draftFieldsFilled = Boolean(
    draftValue.name?.trim() &&
    draftValue.email?.trim() &&
    draftValue.phone?.trim()
  );
  const isInlineEdit = Boolean(isEditing && usesDraftEditing && editSnapshotRef.current);

  const handleFieldChange = (field) => (event) => {
    if (!onChange) return;
    onChange(field, event.target.value);
  };

  const handleDraftFieldChange = (field) => (event) => {
    setDraftValue((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleEditClick = () => {
    if (!usesDraftEditing) {
      editSnapshotRef.current = null;
      setIsEditing(true);
      return;
    }

    const snapshot = {
      name: value?.name ?? '',
      email: value?.email ?? '',
      phone: value?.phone ?? '',
    };
    editSnapshotRef.current = snapshot;
    setDraftValue(snapshot);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    const snapshot = editSnapshotRef.current;
    if (snapshot && onChange) {
      ['name', 'email', 'phone'].forEach((field) => {
        if ((value?.[field] ?? '') !== snapshot[field]) {
          onChange(field, snapshot[field]);
        }
      });
    }
    editSnapshotRef.current = null;
    setIsEditing(false);
  };

  const handleDoneClick = () => {
    if (isInlineEdit) {
      if (!draftFieldsFilled || !onChange) return;
      ['name', 'email', 'phone'].forEach((field) => {
        if ((value?.[field] ?? '') !== draftValue[field]) {
          onChange(field, draftValue[field]);
        }
      });
      editSnapshotRef.current = null;
      setIsEditing(false);
      return;
    }

    if (!allFieldsFilled) return;
    editSnapshotRef.current = null;
    setIsEditing(false);
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    if (usesDraftEditing && editSnapshotRef.current) {
      handleCancelEdit();
      return;
    }
    setIsEditing(false);
  };

  const handleBlur = (event) => {
    if (usesDraftEditing || !isEditing || !allFieldsFilled) return;
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setIsEditing(false);
  };

  /**
   * Show read-only summary when:
   * - All fields are filled AND
   * - User hasn't clicked Edit
   */
  const showReadOnlyMode = collapseWhenComplete && allFieldsFilled && !isEditing;
  const fieldIdPrefix = smsConsentId || 'checkout-contact';

  // Read-only summary view
  if (showReadOnlyMode) {
    return (
      <div
        className={classNames('checkout-contact-fields', 'checkout-contact-readonly', className)}
        data-testid="contact-readonly-summary"
      >
        <div className="contact-readonly-content">
          <div className="contact-readonly-info" aria-label="Contact details">
            <div className="contact-readonly-primary">
              <span className="contact-readonly-name">{value.name}</span>
            </div>
            <div className="contact-readonly-meta">
              <span className="contact-readonly-email">{value.email}</span>
              <span className="contact-readonly-separator" aria-hidden="true">·</span>
              <span className="contact-readonly-phone">{value.phone}</span>
            </div>
          </div>
          <button
            type="button"
            className="contact-edit-button"
            onClick={handleEditClick}
            disabled={disabled}
            aria-label="Change contact details"
          >
            <Pencil aria-hidden="true" size={14} strokeWidth={2} />
          </button>
        </div>
        {showSmsConsent ? (
          <SmsConsentCheckbox
            id={smsConsentId}
            checked={smsConsentAccepted}
            onChange={onSmsConsentChange}
            disabled={disabled}
            error={smsConsentError}
            compact
          />
        ) : null}
      </div>
    );
  }

  // Editable form view
  const activeFieldValue = isInlineEdit ? draftValue : value;
  const handleActiveFieldChange = (field) => (
    isInlineEdit ? handleDraftFieldChange(field) : handleFieldChange(field)
  );

  return (
    <div
      className={classNames(
        'checkout-contact-fields',
        isEditing && 'checkout-contact-fields--editing',
        !usesDraftEditing && 'checkout-contact-fields--live-edit',
        className,
      )}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    >
      <div className="form-group">
        <label className="checkout-contact-field-label" htmlFor={`${fieldIdPrefix}-name`}>
          Name
        </label>
        <input
          id={`${fieldIdPrefix}-name`}
          ref={firstInputRef}
          type="text"
          name="name"
          autoComplete="name"
          placeholder="Full Name"
          value={activeFieldValue?.name ?? ''}
          onChange={handleActiveFieldChange('name')}
          className="checkout-input lumi-input filled"
          disabled={disabled}
          data-hj-allow="true"
          autoFocus={autoFocus}
          aria-invalid={Boolean(invalidFields.name)}
          aria-describedby={invalidFields.name ? 'checkout-error-banner' : undefined}
          required
        />
      </div>

      <div className="form-group">
        <label className="checkout-contact-field-label" htmlFor={`${fieldIdPrefix}-email`}>
          Email
        </label>
        <input
          id={`${fieldIdPrefix}-email`}
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          spellCheck={false}
          autoCapitalize="none"
          placeholder="Email Address"
          value={activeFieldValue?.email ?? ''}
          onChange={handleActiveFieldChange('email')}
          className="checkout-input lumi-input filled"
          disabled={disabled}
          data-hj-allow="true"
          aria-invalid={Boolean(invalidFields.email)}
          aria-describedby={invalidFields.email ? 'checkout-error-banner' : undefined}
          required
        />
      </div>

      <div className="form-group">
        <label className="checkout-contact-field-label" htmlFor={`${fieldIdPrefix}-phone`}>
          Phone
        </label>
        <input
          id={`${fieldIdPrefix}-phone`}
          type="tel"
          name="phone"
          autoComplete="tel"
          inputMode="tel"
          placeholder="Phone Number"
          value={activeFieldValue?.phone ?? ''}
          onChange={handleActiveFieldChange('phone')}
          className="checkout-input lumi-input filled"
          disabled={disabled}
          data-hj-allow="true"
          aria-invalid={Boolean(invalidFields.phone)}
          aria-describedby={invalidFields.phone ? 'checkout-error-banner' : undefined}
          required
        />
      </div>

      {showSmsConsent ? (
        <SmsConsentCheckbox
          id={smsConsentId}
          checked={smsConsentAccepted}
          onChange={onSmsConsentChange}
          disabled={disabled}
          error={smsConsentError}
        />
      ) : null}

      {usesDraftEditing && isEditing && editSnapshotRef.current ? (
        <div className="contact-edit-actions">
          <button
            type="button"
            className="contact-edit-action contact-edit-action--secondary"
            onClick={handleCancelEdit}
            disabled={disabled}
          >
            Discard
          </button>
          <button
            type="button"
            className="contact-edit-action contact-edit-action--primary"
            onClick={handleDoneClick}
            disabled={disabled || !draftFieldsFilled}
          >
            Save
          </button>
        </div>
      ) : null}
    </div>
  );
}

CheckoutContactFields.propTypes = {
  value: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
  }),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
  smsConsentAccepted: PropTypes.bool,
  onSmsConsentChange: PropTypes.func,
  smsConsentError: PropTypes.string,
  smsConsentId: PropTypes.string,
  showSmsConsent: PropTypes.bool,
  collapseWhenComplete: PropTypes.bool,
  editBehavior: PropTypes.oneOf(['draft', 'live']),
  invalidFields: PropTypes.shape({
    name: PropTypes.bool,
    email: PropTypes.bool,
    phone: PropTypes.bool,
  }),
};
